import { useEffect } from 'react';
import { print } from 'graphql';
import { useQuery, QueryResult, gql } from '@apollo/client';
import { AppId } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { GetOfferDetailsQuery, GetOfferDetailsQueryVariables } from '@/lib/graph-ql';
import * as Fragment from './Fragments';
import useAuth from '../useAuth';

export const OFFER_FRAGMENT = gql`
  fragment offerFragment on CustomerPackageOffer {
    id
    title
    synopsis
    heroImage {
      uri
    }
    logoImages {
      uri
    }
    groupsPage(first: 1) {
      groups {
        title
        contentPage {
          ...contentPageCommonFragment
        }
        aspectRatio
      }
    }
    price {
      basePrice {
        amount
      }
      billingFrequency
      discount {
        type
        value
        duration
      }
    }
    collectionLink {
      collection {
        id
        title
      }
    }
  }
  ${Fragment.CONTENT_PAGE_FRAGMENT}
  ${Fragment.CONTENT_PAGE_COMMON_FRAGMENT}
  ${Fragment.IMAGE_DETAILS_FRAGMENT}
  ${Fragment.TILE_IMAGE_FRAGMENT}
  ${Fragment.LINEAR_CHANNEL_TILE_FRAGMENT}
  ${Fragment.VIEWING_METHODS_WITHOUT_OFFER}
`;

export const GET_OFFER_DETAILS = gql`
  query GetOfferDetails($appId: AppId!, $offerId: ID!) {
    experience(appId: $appId) {
      customer {
        offer(id: $offerId) {
          ...offerFragment
        }
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const OFFER_DETAILS_EVENT_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription offerDetailsEventSubscription($appId: AppId!, $offerId: ID!) {
    customerAccount(appId: $appId) {
      account {
        offer(id: $offerId) {
          ...offerFragment
        }
      }
    }
  }
  ${print(OFFER_FRAGMENT)}
`);
const useOfferDetails = (
  offerId: string,
): QueryResult<GetOfferDetailsQuery, GetOfferDetailsQueryVariables> => {
  const result = useQuery(GET_OFFER_DETAILS, {
    variables: { appId: AppId.SkyGoWeb, offerId },
    fetchPolicy: 'network-only',
  });
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (isAuthenticated && result.data && result.subscribeToMore) {
      unsubscribe = result.subscribeToMore({
        document: OFFER_DETAILS_EVENT_SUBSCRIPTION,
        variables: { appId: AppId.SkyGoWeb, offerId },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }
          const account = subscriptionData.data?.customerAccount?.account;

          return {
            ...prev,
            experience: {
              ...prev.experience,
              customer: {
                ...prev.experience.customer,
                offer: account?.offer,
              },
            },
          };
        },
      });
    }

    // Return the cleanup function (either unsubscribe or a no-op if it's undefined)
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [result, offerId, isAuthenticated]);

  return result;
};
export default useOfferDetails;
